import { cn } from '@/lib/utils';

/**
 * What a cafe with no photo of its own looks like.
 *
 * WHAT THIS REPLACES. `getCafeImageUrl` falls back to `getStockImage(id)`, a
 * hash of the cafe id into 42 Unsplash photos of lattes, matcha and pastries.
 * The comment above that list says they are "clearly product/lifestyle photos
 * that won't be mistaken for actual cafe interiors". At the sizes cards
 * actually use, 40px on the map and a thumbnail in the browse list, that intent
 * fails completely: a photo of a flat white in a card labelled with a cafe's
 * name reads as that cafe's photo. 70 of the 977 cafes on the map have no photo
 * of their own, so it is not a rare state.
 *
 * A tile with the cafe's initial cannot be mistaken for a photograph, which is
 * the entire point. It is deterministic, it needs no network request, and it
 * degrades to a readable letter at any size.
 *
 * An admin chosen `placeholder_index` is still honoured and still shows the
 * stock image it names. That is a deliberate editorial choice about a specific
 * cafe; this is the fallback for when nobody has chosen anything.
 */
export function CafePhotoPlaceholder({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  // Take the first letter that is actually a letter or digit, so a cafe named
  // "'sup Coffee" shows S rather than an apostrophe.
  const initial = (name.match(/[\p{L}\p{N}]/u)?.[0] ?? '?').toUpperCase();

  return (
    <div
      className={cn(
        'flex h-full w-full items-center justify-center bg-muted select-none',
        className,
      )}
      // Decorative: the cafe name is already rendered as text next to this in
      // every card that uses it, so announcing the initial repeats it.
      aria-hidden="true"
      data-testid="cafe-photo-placeholder"
    >
      <span className="font-semibold leading-none text-muted-foreground text-[length:min(44%,1.75rem)]">
        {initial}
      </span>
    </div>
  );
}
